
import {
  watch, defineComponent, ref, nextTick, onMounted, onUnmounted,
} from 'vue';

import {
  downLoad, factorColorRender, windDirection, factorTxtRenderByColor, factorValueRender,
} from '@/utils/index';

import * as echarts from 'echarts';

import moment from 'moment';

import html2canvas from 'html2canvas';

export default defineComponent({
  props: {
    airType: {
      type: String,
      default: () => 'aqi',
      require: true,
    },
    radarDataList: {
      type: Object,
      default: Object,
      require: true,
    },
  },
  setup (props) {
    const unit = () => {
      if (props.airType === 'aqi') {
        return '';
      }

      if (props.airType === 'co') {
        return 'mg/m³';
      }

      return 'μg/m³';
    };

    const lengedList = ref<any>([]);

    const analysisChart = ref<any>(null);
    const noDataShow = ref<boolean>(false);
    const unitShow = ref<boolean>(false);
    let chart: any = null;
    // 雷达全量数据
    const fullData = ref([]);

    const directions = [
      '0°',
      '30°',
      '60°',
      '90°',
      '120°',
      '150°',
      '180°',
      '210°',
      '240°',
      '270°',
      '300°',
      '330°',
    ];

    // 要素对应文字
    const factorKey = {
      pm25: 'PM₂.₅',
      pm10: 'PM₁₀',
      so2: 'SO₂',
      no2: 'NO₂',
      co: 'CO',
      o3: 'O₃',
      aqi: 'AQI',
    };

    const option = {
      polar: {},
      tooltip: {
        show: true,
        formatter: (params: any) => `<div style="text-align: left;"><div>${moment(params.value[3], 'YYYYMMDDHH').format('MM-DD HH:00')}</div><div style="textAlign: left;">风速: ${params.value[0]
        }m/s</div><div>风向: ${windDirection(params.value[1])
        }</div><div>${factorKey[props.airType]}: ${params.value[2]}${unit()}</div></div>`
        ,
      },
      angleAxis: {
        type: 'value',
        data: directions,
        formatter: (val: any) => `${val}°`,
        boundaryGap: false,
        splitLine: {
          show: true,
        },
        axisLine: {
          show: false,
        },
      },
      radiusAxis: {
        // name: 'm/s',
        type: 'value',
        // data: days,
        axisLine: {
          show: false,
        },
        axisLabel: {
          rotate: 45,
        },
      },
      legend: {
        show: false,
        data: [],
      },
      series: {
        // name: 'Punch Card',
        type: 'scatter',
        coordinateSystem: 'polar',
        itemStyle: {
          color: (val: any) => factorColorRender(val.data[2], props.airType),
        },
        symbolSize: 6,
        data: [],
        animationDelay (idx: any) {
          return idx * 5;
        },
      },
    };

    /**
     *  重置图表
     * @param {any} val value
     * @return {void}
     */
    const reSize = (): any => {
      if (Object.keys(chart).length) {
        chart.resize();
      }
    };

    /**
     *  配置线状图
     * @param {any} val 配置项
     * @return {void}
     */
    const setChart = (val: any) => {
      nextTick(() => {
        if (val && Object.keys(chart).length) {
          // 不和之前的option合并
          chart.setOption(val, true);
          reSize();
        }
      });
    };

    const drawChart = (val: any): void => {
      fullData.value = val[props.airType].map((item: any) => [Number(item[0]),
        Number(item[1].slice(0, item[1].length - 1)),
        Number(item[2]),
        Number(item[3]),]);
      option.series.data = fullData.value;
      setChart(option);
      const colorArr = fullData.value.map((item) => factorColorRender(item[2], props.airType));

      lengedList.value = [...new Set(colorArr),].map((item) => ({
        level: factorTxtRenderByColor(item, props.airType),
        color: item,
        flag: true,
      }));
    };

    const levelFilter = (val: string) => {
      let i = 0;

      lengedList.value.forEach((item: any, index: number) => {
        if (item.color === val) {
          i = index;
        }
      });

      if (lengedList.value[i].flag) {
        const arr = option.series.data.filter((item: any) => factorColorRender(item[2], props.airType) !== val);

        option.series.data = arr;

        lengedList.value[i].flag = false;

        if (!option.series.data.length) {
          unitShow.value = true;
        }
      } else {
        const arr = fullData.value.filter((item: any) => factorColorRender(item[2], props.airType) === val);

        option.series.data = [...option.series.data, ...arr,];
        lengedList.value[i].flag = true;
        if (option.series.data.length) {
          unitShow.value = false;
        }
      }

      setChart(option);

      // current.flag = !current.flag;
    };

    /**
     * 初始化图表
     * @returns {void}
     */
    const initCharts = () => {
      const chartContainer = document.getElementById('radar-container') as HTMLElement;

      chart = echarts.init(chartContainer);
      window.addEventListener('resize', reSize);
    };

    /**
     *  下载图表
     * @return {void}
     */
    const downloadChart = (): any => {
      if (analysisChart.value) {
        html2canvas(analysisChart.value, {
          width: analysisChart.value.clientWidth,
          height: analysisChart.value.clientHeight,
          scrollY: 0,
          scrollX: 0,
          scale: 0.8,
          useCORS: true,
          allowTaint: false,
        }).then((canvas) => {
          const base64 = canvas.toDataURL('image/png');

          downLoad(base64, `${props.airType.toUpperCase()}浓度分布雷达图`);
        });
      }
    };

    watch(() => props.radarDataList, (cur: any) => {
      if (Object.keys(cur).length) {
        noDataShow.value = false;
        setTimeout(() => {
          drawChart(cur);
        });
      } else {
        noDataShow.value = true;
      }
    }, {
      deep: true,
      immediate: true,
    });

    watch(() => props.airType, (cur: any) => {
      if (cur) {
        setTimeout(() => {
          if (props.radarDataList && Object.keys(props.radarDataList).length) {
            drawChart(props.radarDataList);
          }
        });
      }
    });

    /**
     * 挂载图表
     */
    onMounted(() => {
      initCharts();
    });

    /**
     * 销毁图表
     */
    onUnmounted(() => {
      window.removeEventListener('resize', reSize);

      echarts.dispose(chart);
      chart = null;
    });

    return {
      props,
      factorKey,
      noDataShow,
      analysisChart,
      downloadChart,
      levelFilter,
      unitShow,
      lengedList,
    };
  },
});

